import {isUndefined} from 'lodash';

import {RendererOf} from '../../../../../libs/shared/core/src/helpers/react/reactHelpers';
import {CancelToken} from '../../../../../libs/shared/core/src/helpers/utilities/asyncCancelHelpers';
import {DropdownResults, DropdownSource, RowHeightPredicate, RowRendererOf} from './dropdownSource';

export interface DropdownFetchResult<T, U> {
  items: ReadonlyArray<T>;
  canLoadMore: boolean;
  paginationData?: U;
}

export type DropdownFetcher<T, U = any> = (
  query: string,
  items: ReadonlyArray<T>,
  cancelToken: CancelToken,
  paginationData: U | undefined,
) => Promise<DropdownFetchResult<T, U>>;

export interface AsyncDropboxSourceOptions<T, U = void> {
  fetcher: DropdownFetcher<T, U>;
  renderRow: RowRendererOf<T>;
  renderEmpty?: RendererOf<string>;
  initialItems?: ReadonlyArray<T>;
  rowHeightPredicate?: RowHeightPredicate<T>;
}

export class AsyncDropdownSource<T, U = void> implements DropdownSource {
  constructor(options: AsyncDropboxSourceOptions<T, U>) {
    this.fetcher = options.fetcher;
    this.rowRenderer = options.renderRow;
    this.emptyRenderer = options.renderEmpty;
    this.initialItems = options.initialItems;
    this.rowHeightPredicate = options.rowHeightPredicate;
  }

  private readonly fetcher: DropdownFetcher<T, U>;
  private readonly rowRenderer: RowRendererOf<T>;
  private readonly emptyRenderer?: RendererOf<string>;
  private readonly initialItems?: ReadonlyArray<T>;
  private readonly rowHeightPredicate?: RowHeightPredicate<T>;

  async searchAsync(query: string, cancelToken: CancelToken) {
    // If we have initial items, return right away.
    if (this.initialItems && !query) {
      const initialResults = {items: this.initialItems, canLoadMore: true};
      return new AsyncDropdownResults(
        this.fetcher,
        this.rowRenderer,
        this.emptyRenderer,
        query,
        initialResults,
        this.rowHeightPredicate,
      );
    }

    // Otherwise, use the fetcher.
    const result = await this.fetcher(query, [], cancelToken, undefined);
    return new AsyncDropdownResults(
      this.fetcher,
      this.rowRenderer,
      this.emptyRenderer,
      query,
      result,
      this.rowHeightPredicate,
    );
  }
}

class AsyncDropdownResults<T, U> implements DropdownResults {
  constructor(
    fetcher: DropdownFetcher<T, U>,
    rowRenderer: RowRendererOf<T>,
    emptyRenderer: RendererOf<string> | undefined,
    query: string,
    result: DropdownFetchResult<T, U>,
    rowHeightPredicate?: RowHeightPredicate<T>,
  ) {
    this.fetcher = fetcher;
    this.rowRenderer = rowRenderer;
    this.emptyRenderer = emptyRenderer;
    this.rowHeightPredicate = rowHeightPredicate;
    this.query = query;
    this.items = result.items;
    this.canLoadMore = result.canLoadMore;
    this.paginationData = result.paginationData;
  }

  private readonly fetcher: DropdownFetcher<T, U>;
  private readonly rowRenderer: RowRendererOf<T>;
  private readonly emptyRenderer?: RendererOf<string>;
  private readonly rowHeightPredicate?: RowHeightPredicate<T>;
  private readonly items: ReadonlyArray<T>;
  private readonly paginationData: U | undefined;

  get rowCount() {
    return this.items.length;
  }

  readonly query: string;
  readonly canLoadMore: boolean;

  renderRow(index: number) {
    const item = this.items[index];
    if (isUndefined(item)) {
      throw new Error('An item at the specified index could not be found.');
    }

    return this.rowRenderer(item, index, this.items, this.query.length > 0);
  }

  getRowHeight(index: number) {
    if (!this.rowHeightPredicate) {
      return undefined;
    }

    // If `canLoadMore` is truthy and `index` equals the length of this.items, it means we're trying to get loader row height.
    const item = this.canLoadMore && index === this.items.length ? null : this.items[index];

    if (isUndefined(item)) {
      throw new Error('An item at the specified index could not be found.');
    }

    return this.rowHeightPredicate(item, index, this.items, this.query.length > 0);
  }

  renderEmpty() {
    return this.emptyRenderer && this.emptyRenderer(this.query);
  }

  async loadMoreAsync(cancelToken: CancelToken) {
    const result = await this.fetcher(this.query, this.items, cancelToken, this.paginationData);
    return new AsyncDropdownResults(
      this.fetcher,
      this.rowRenderer,
      undefined,
      this.query,
      result,
      this.rowHeightPredicate,
    );
  }
}
